/**
 * Mappers to transform frontend form data to backend API format
 * and reverse-mappers to hydrate forms from backend responses.
 *
 * We keep mappings 1-to-1 as much as possible. Fields that match between
 * frontend and backend are passed through directly. Only key renames are
 * applied where frontend form field names differ from backend API field names.
 * Value transformations are no longer needed since backend now stores
 * the same enum keys as the frontend form values.
 */

/**
 * Transform freelancer basic details form data to API format
 * @param {object} formData - The freelancer basic details form data
 * @returns {object} The mapped data in API format
 */
export const mapFreelancerBasicDetails = (formData) => {
  const {
    country,
    is_pep,
    monthly_volume,
    custom_industry,
    pep_full_name,
    pep_relationship,
    pep_position,
    pep_position_status,
    pep_country,
    pep_relationship_specify,
    ...rest
  } = formData;

  const mapped = {
    ...rest,
    country_of_operation: country,
    pep_status: is_pep,
    monthly_transaction_volume: monthly_volume,
    industry_other: custom_industry || null
  };

  // Handle PEP fields conditionally
  if (is_pep === 'yes') {
    mapped.full_name = pep_full_name;
    mapped.relationship_to_govt_official = pep_relationship;
    mapped.position_office_held = pep_position;
    mapped.position_status = pep_position_status;
    mapped.country_of_position = pep_country;

    // Handle "Others" PEP relationship
    if (pep_relationship === 'others' && pep_relationship_specify) {
      mapped.relationship_other = pep_relationship_specify;
    }
  }

  return mapped;
};

/**
 * Transform freelancer additional info form data to API format
 * @param {object} formData - The freelancer additional info form data
 * @param {object} attachments - The attachment IDs mapping
 * @returns {object} The mapped data in API format
 */
export const mapFreelancerAdditionalInfo = (formData, attachments) => {
  const mapped = {
    platform_link: formData.platform_link || null
  };

  // Map file attachments
  if (attachments.identification_document) {
    mapped.means_of_identification_attachment_id = attachments.identification_document;
  }
  if (attachments.proof_of_address) {
    mapped.proof_of_address_attachment_id = attachments.proof_of_address;
  }
  if (attachments.platform_evidence) {
    mapped.platform_evidence_attachment_id = attachments.platform_evidence;
  }

  return mapped;
};

/**
 * Transform corporate business details form data to API format
 * @param {object} formData - The corporate business details form data
 * @param {object} attachments - The attachment IDs mapping
 * @returns {object} The mapped data in API format
 */
export const mapCorporateBusinessDetails = (formData, attachments) => {
  const { country, monthly_volume, custom_industry, ...rest } = formData;

  const mapped = {
    ...rest,
    country_of_operation: country,
    monthly_transaction_volume: monthly_volume,
    industry_other: custom_industry || null
  };

  if (attachments?.proof_of_business_address) {
    mapped.proof_of_address_attachment_id = attachments.proof_of_business_address;
  }

  return mapped;
};

/**
 * Transform director data to API format
 * @param {object} directorData - The director form data
 * @returns {object} The mapped data in API format
 */
export const mapDirector = (directorData) => {
  return {
    full_name: directorData.name,
    pep_status: directorData.is_pep,
    bvn: directorData.bvn
  };
};

/**
 * Transform shareholder data to API format
 * @param {object} shareholderData - The shareholder form data
 * @returns {object} The mapped data in API format
 */
export const mapShareholder = (shareholderData) => {
  return {
    full_name: shareholderData.name,
    pep_status: shareholderData.is_pep
  };
};

/**
 * Transform UBO data to API format
 * @param {object} uboData - The UBO form data
 * @param {string} attachmentId - The attachment ID for UBO identification document
 * @returns {object} The mapped data in API format
 */
export const mapUBO = (uboData, attachmentId) => {
  return {
    full_name: uboData.name,
    pep_status: uboData.is_pep,
    id_type: uboData.id_type,
    id_number: uboData.id_number,
    means_of_identification_attachment_id: attachmentId
  };
};

/**
 * Transform PEP declaration entry to API format
 * @param {object} pepData - The PEP declaration form data
 * @returns {object} The mapped data in API format
 */
export const mapPepDeclaration = (pepData) => {
  // Handle "no PEPs" confirmation
  if (pepData.pep_confirmed_none === true || pepData.pep_confirmed_none === 'true') {
    return {
      pep_confirmed_none: true
    };
  }

  const mapped = {
    full_name: pepData.full_name,
    position_in_org: pepData.position,
    relationship_to_govt_official: pepData.relationship,
    position_office_held: pepData.position_held,
    position_status: pepData.position_status,
    country_of_position: pepData.country,
    source_of_funds: pepData.source_of_funds
  };

  // Handle "Others" relationship
  if (pepData.relationship === 'others' && pepData.specify_relationship) {
    mapped.relationship_other = pepData.specify_relationship;
  }

  return mapped;
};

/**
 * Transform compliance docs form data to API format
 * @param {object} formData - The compliance docs form data
 * @param {object} attachments - The attachment IDs mapping
 * @returns {object} The mapped data in API format
 */
export const mapComplianceDocs = (formData, attachments) => {
  const mapped = {
    certificate_of_incorporation_attachment_id: attachments.certificate_of_incorporation,
    memorandum_articles_attachment_id: attachments.memorandum_of_association
  };

  // Operating license (required for FI, optional for Non-FI)
  if (attachments.operating_license) {
    mapped.valid_operating_license_attachment_id = attachments.operating_license;
  }

  // AML policy (conditional based on industry)
  if (attachments.aml_policy_document) {
    mapped.aml_policy_attachment_id = attachments.aml_policy_document;
  }

  // AML attestation checkbox (for Non-FI in non-AML industries)
  if (formData.amlConfirmed) {
    mapped.aml_attestation_confirmed =
      formData.amlConfirmed === true || formData.amlConfirmed === 'true';
  }

  // SCUML certificate (for DNFI)
  if (attachments.scuml_certificate) {
    mapped.scuml_attachment_id = attachments.scuml_certificate;
  }

  return mapped;
};

/**
 * Transform due diligence questionnaire to API format
 * @param {object} formData - The due diligence form data
 * @returns {object} The mapped data in API format
 */
export const mapDueDiligence = (formData) => {
  const mapped = {
    // Page 1 — General AML
    has_aml_program: formData.has_aml_program,
    has_compliance_officer: formData.has_compliance_officer,
    has_documented_policies: formData.has_documented_policies,
    has_internal_audit: formData.has_internal_audit,
    has_enforcement_actions: formData.has_enforcement_actions,
    // Page 2 — KYC / Due Diligence
    has_kyc_policy: formData.has_kyc_policy,
    has_identification_procedures: formData.has_identification_procedures,
    has_record_retention: formData.has_record_retention,
    has_ofac_screening: formData.has_ofac_screening,
    has_customer_updates: formData.has_customer_updates,
    // Page 3 — SAR + AML Training
    has_sar_policies: formData.has_sar_policies,
    has_aml_training: formData.has_aml_training,
    has_training_records: formData.has_training_records,
    has_aml_updates: formData.has_aml_updates,
    provides_third_party_training: formData.provides_third_party_training,
    // Page 4 — Anti-Bribery / Anti-Corruption
    has_abc_policy: formData.has_abc_policy,
    has_govt_official: formData.has_govt_official,
    has_gifts_policy: formData.has_gifts_policy,
    has_political_contributions: formData.has_political_contributions,
    has_abc_reporting_system: formData.has_abc_reporting_system,
    has_abc_training: formData.has_abc_training,
    certified: formData.certified
  };

  // Conditional fields — compliance officer details
  if (formData.has_compliance_officer === 'yes') {
    mapped.compliance_officer_name = formData.compliance_officer_name || '';
    mapped.compliance_officer_designation = formData.compliance_officer_designation || '';
    mapped.compliance_officer_email = formData.compliance_officer_email || '';
    mapped.compliance_officer_phone = formData.compliance_officer_phone || '';
  }

  // Conditional — enforcement details
  if (formData.has_enforcement_actions === 'yes') {
    mapped.enforcement_details = formData.enforcement_details || '';
  }

  // Conditional — retention period
  if (formData.has_record_retention === 'yes') {
    mapped.retention_period = formData.retention_period || '';
  }

  // Conditional — update frequency
  if (formData.has_customer_updates === 'yes') {
    mapped.update_frequency = formData.update_frequency || '';
  }

  // Conditional — govt official details
  if (formData.has_govt_official === 'yes') {
    mapped.govt_official_name = formData.govt_official_name || '';
    mapped.govt_official_association = formData.govt_official_association || '';
  }

  return mapped;
};

// ─── Reverse mappers (backend → frontend form values) ─────────────────────────

/**
 * Convert backend FREELANCER_BASIC_DETAILS entry data back to form-compatible values.
 * @param {object} backendData - The backend FREELANCER_BASIC_DETAILS entry data
 * @returns {object} The form-compatible data
 */
export const unmapFreelancerBasicDetails = (backendData) => {
  return {
    brand_name: backendData.brand_name || '',
    industry: backendData.industry || '',
    custom_industry: backendData.industry_other || '',
    nature_of_business: backendData.nature_of_business || '',
    business_address: backendData.business_address || '',
    country: backendData.country_of_operation || '',
    bvn: backendData.bvn || '',
    id_type: backendData.id_type || '',
    id_number: backendData.id_number || '',
    is_pep: backendData.pep_status || '',
    monthly_volume: backendData.monthly_transaction_volume || '',
    source_of_funds: backendData.source_of_funds || '',
    // PEP detail fields (only present when pep_status === 'yes')
    pep_full_name: backendData.full_name || '',
    pep_relationship: backendData.relationship_to_govt_official || '',
    pep_relationship_specify: backendData.relationship_other || '',
    pep_position: backendData.position_office_held || '',
    pep_position_status: backendData.position_status || '',
    pep_country: backendData.country_of_position || ''
  };
};

/**
 * Convert backend BUSINESS_DETAILS entry data back to corporate form-compatible values.
 * @param {object} data - The backend BUSINESS_DETAILS entry data
 * @param {string} entryId - The entry ID
 * @returns {object} The form-compatible data
 */
export const unmapDirector = (data, entryId) => ({
  id: entryId,
  name: data.full_name,
  is_pep: data.pep_status || 'no',
  bvn: data.bvn
});

/**
 * Convert backend shareholder data back to form-compatible values.
 * @param {object} data - The backend shareholder data
 * @param {string} entryId - The entry ID
 * @returns {object} The form-compatible data
 */
export const unmapShareholder = (data, entryId) => ({
  id: entryId,
  name: data.full_name,
  is_pep: data.pep_status || 'no'
});

/**
 * Convert backend UBO data back to form-compatible values.
 * @param {object} data - The backend UBO data
 * @param {string} entryId - The entry ID
 * @param {object} remoteFile - The remote file object for the identification document
 * @returns {object} The form-compatible data
 */
export const unmapUBO = (data, entryId, remoteFile) => ({
  id: entryId,
  name: data.full_name,
  is_pep: data.pep_status || 'no',
  id_type: data.id_type,
  id_number: data.id_number,
  means_of_id_file: remoteFile || null,
  means_of_identification_attachment_id: data.means_of_identification_attachment_id || null,
  version: data.version
});

/**
 * Convert backend PEP entry data back to form-compatible values.
 * @param {object} data - The backend PEP entry data
 * @param {string} entryId - The entry ID
 * @returns {object} The form-compatible data
 */
export const unmapPepEntry = (data, entryId) => ({
  id: entryId,
  full_name: data.full_name,
  position: data.position_in_org,
  relationship: data.relationship_to_govt_official,
  specify_relationship: data.relationship_other,
  position_held: data.position_office_held,
  position_status: data.position_status,
  country: data.country_of_position,
  source_of_funds: data.source_of_funds
});

/**
 * Convert backend due diligence data back to form-compatible values.
 * @param {object} data - The backend due diligence data
 * @returns {object} The form-compatible data
 */
export const unmapDueDiligence = (data) => ({
  has_aml_program: data.has_aml_program || '',
  has_compliance_officer: data.has_compliance_officer || '',
  compliance_officer_name: data.compliance_officer_name || '',
  compliance_officer_designation: data.compliance_officer_designation || '',
  compliance_officer_email: data.compliance_officer_email || '',
  compliance_officer_phone: data.compliance_officer_phone || '',
  has_documented_policies: data.has_documented_policies || '',
  has_internal_audit: data.has_internal_audit || '',
  has_enforcement_actions: data.has_enforcement_actions || '',
  enforcement_details: data.enforcement_details || '',
  has_kyc_policy: data.has_kyc_policy || '',
  has_identification_procedures: data.has_identification_procedures || '',
  has_record_retention: data.has_record_retention || '',
  retention_period: data.retention_period || '',
  has_ofac_screening: data.has_ofac_screening || '',
  has_customer_updates: data.has_customer_updates || '',
  update_frequency: data.update_frequency || '',
  has_sar_policies: data.has_sar_policies || '',
  has_aml_training: data.has_aml_training || '',
  has_training_records: data.has_training_records || '',
  has_aml_updates: data.has_aml_updates || '',
  provides_third_party_training: data.provides_third_party_training || '',
  has_abc_policy: data.has_abc_policy || '',
  has_govt_official: data.has_govt_official || '',
  govt_official_name: data.govt_official_name || '',
  govt_official_association: data.govt_official_association || '',
  has_gifts_policy: data.has_gifts_policy || '',
  has_political_contributions: data.has_political_contributions || '',
  has_abc_reporting_system: data.has_abc_reporting_system || '',
  has_abc_training: data.has_abc_training || '',
  certified: data.certified || false
});

/**
 * Convert backend BUSINESS_DETAILS entry data back to form-compatible values.
 * @param {object} backendData - The backend BUSINESS_DETAILS entry data
 * @returns {object} The form-compatible data
 */
export const unmapCorporateBusinessDetails = (backendData) => {
  return {
    operating_business_name: backendData.operating_business_name || '',
    industry: backendData.industry || '',
    custom_industry: backendData.industry_other || '',
    nature_of_business: backendData.nature_of_business || '',
    business_address: backendData.business_address || '',
    country: backendData.country_of_operation || '',
    monthly_volume: backendData.monthly_transaction_volume || '',
    website_link: backendData.website_link || '',
    proof_of_business_address: backendData.proof_of_address_attachment_id || null
  };
};
